"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import {
  doc,
  increment,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/auth-context";
import { setIsDesignPartner } from "../lib/errorContext";
import { useActiveProfileStore } from "@eventify/shared/state/activeProfileStore";
import { useTeamProfilesStore } from "@eventify/shared/state/teamProfilesStore";
import { enqueueEvent, setEventBufferContext } from "../lib/eventBuffer";

const SESSION_DEBOUNCE_MS = 5 * 60 * 1000;
const SCREEN_DEDUPE_MS = 1000;

const isDesignPartnerCache: Record<string, boolean> = {};
const isDeepTracedCache: Record<string, boolean> = {};

export function WebActivityTracker() {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  const role = user?.role ?? null;
  const pathname = usePathname();

  const activeProfileId = useActiveProfileStore((s) => s.activeProfileId);
  const profiles = useTeamProfilesStore((s) => s.profiles);
  const activeProfile = activeProfileId
    ? profiles.find((p) => p.id === activeProfileId) ?? null
    : null;
  const profileName = activeProfile?.name ?? null;

  const lastActivityWriteRef = useRef(0);
  const sessionStartRef = useRef(Date.now());
  const lastScreenWriteRef = useRef<{ name: string; ts: number }>({ name: "", ts: 0 });

  // Session tracking — mirror of mobile App.tsx:518-554
  useEffect(() => {
    if (!uid) return;
    const userRef = doc(db, "users", uid);

    const writeActive = () => {
      const now = Date.now();
      if (now - lastActivityWriteRef.current < SESSION_DEBOUNCE_MS) return;
      lastActivityWriteRef.current = now;
      updateDoc(userRef, { lastActiveAt: serverTimestamp() }).catch(() => {});
    };

    sessionStartRef.current = Date.now();
    writeActive();
    updateDoc(userRef, { sessionCount: increment(1) }).catch(() => {});

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        sessionStartRef.current = Date.now();
        writeActive();
      } else {
        const sec = Math.round((Date.now() - sessionStartRef.current) / 1000);
        if (sec > 2 && sec < 86400) {
          updateDoc(userRef, {
            lastActiveAt: serverTimestamp(),
            totalTimeInApp: increment(sec),
          }).catch(() => {});
        }
      }
    };

    const onBeforeUnload = () => {
      const sec = Math.round((Date.now() - sessionStartRef.current) / 1000);
      if (sec > 2 && sec < 86400) {
        updateDoc(userRef, {
          lastActiveAt: serverTimestamp(),
          totalTimeInApp: increment(sec),
        }).catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [uid]);

  // Subscribe to the two flags — caches the values so writers can early-return.
  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        const dpFlag = snap.data()?.isDesignPartner === true;
        const dtFlag = snap.data()?.isDeepTraced === true;
        isDesignPartnerCache[uid] = dpFlag;
        isDeepTracedCache[uid] = dtFlag;
        setIsDesignPartner(uid, dpFlag);
        setEventBufferContext({
          uid,
          enabled: dpFlag,
          vendorId: role === "vendor" ? uid : null,
          profileId: activeProfileId,
          profileName,
        });
      },
      () => {}
    );
    return () => unsub();
  }, [uid, role, activeProfileId, profileName]);

  // Keep buffer context current as profile changes mid-session, even before
  // the next snapshot fires.
  useEffect(() => {
    if (!uid) return;
    setEventBufferContext({
      uid,
      enabled: isDesignPartnerCache[uid] === true,
      vendorId: role === "vendor" ? uid : null,
      profileId: activeProfileId,
      profileName,
    });
  }, [uid, role, activeProfileId, profileName]);

  // Screen-view writer — only when design partner. Dedupe per-pathname.
  useEffect(() => {
    if (!uid || !pathname) return;
    if (!isDesignPartnerCache[uid]) return;

    const last = lastScreenWriteRef.current;
    if (last.name === pathname && Date.now() - last.ts < SCREEN_DEDUPE_MS) return;
    lastScreenWriteRef.current = { name: pathname, ts: Date.now() };

    enqueueEvent({ type: "screen", name: pathname });
  }, [uid, pathname]);

  // Deep-trace click capture — global listener, only when isDeepTraced flips on.
  useEffect(() => {
    if (!uid) return;
    if (!isDesignPartnerCache[uid]) return;
    if (!isDeepTracedCache[uid]) return;

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const el = target.closest("[data-track-id], button, a, [role=button]") as HTMLElement | null;
      if (!el) return;

      const dataLabel = el.dataset.trackId;
      const aria = el.getAttribute("aria-label");
      const text = el.textContent?.trim().replace(/\s+/g, " ");
      const label =
        dataLabel ||
        aria ||
        (text ? text.slice(0, 60) : null) ||
        el.tagName.toLowerCase();

      enqueueEvent({ type: "click", name: label });
    };

    document.addEventListener("click", onClick, { capture: true, passive: true });
    return () => {
      document.removeEventListener("click", onClick, { capture: true });
    };
    // isDeepTracedCache is keyed off uid and re-read in the snapshot effect,
    // so we re-attach whenever the snapshot effect re-runs. activeProfileId
    // included so a profile switch re-evaluates the gate cleanly.
  }, [uid, role, activeProfileId, profileName]);

  return null;
}
